import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  limit as qlimit,
  onSnapshot,
  getDocs,
  where,
  deleteDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  doc as fsDoc,
} from 'firebase/firestore';
import { ref as storageRef, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import { categoryLabel } from '../../lib/format.js';
import { CATEGORIES, type Category } from '@sfp/shared/roles';

type FileSortKey = 'name' | 'entity' | 'size' | 'status';

interface CustomFolder {
  id: string;
  level: 'category' | 'docType';
  categoryKey: string;
  docType?: string;
  label: string;
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 50) || 'folder';
}

interface DocRow {
  id: string;
  originalName?: string;
  category?: string;
  docType?: string;
  filingEntity?: string;
  sizeBytes?: number;
  status?: string;
  storagePath?: string;
  uploadedAt?: { seconds: number };
}

/**
 * Classification-driven virtual file browser. Folders are the LLM's
 * classification — Category → Doc Type → the documents (real filenames) — built
 * live from Firestore `documents`, NOT the opaque docId storage layout. Deleting
 * here purges the doc record, its storage object, and its financial rows.
 */
export function ClassifiedFiles() {
  const { role } = useAuth();
  const isAdmin = role === 'admin';
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [vpath, setVpath] = useState<string[]>([]); // [], [category], [category, docType]
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [progress, setProgress] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<FileSortKey>('name');
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const [moveOpen, setMoveOpen] = useState(false);
  const [customFolders, setCustomFolders] = useState<CustomFolder[]>([]);

  useEffect(() => {
    const u1 = onSnapshot(
      query(collection(db, 'documents'), orderBy('uploadedAt', 'desc'), qlimit(3000)),
      (snap) => {
        setDocs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DocRow, 'id'>) })));
        setLoading(false);
      },
      () => setLoading(false),
    );
    // Persisted, manually-created folders (categories / doc types) so empty
    // ones still show — Move a file in to populate them.
    const u2 = onSnapshot(
      query(collection(db, 'classifiedFolders'), qlimit(500)),
      (snap) => setCustomFolders(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CustomFolder, 'id'>) }))),
      () => setCustomFolders([]),
    );
    return () => { u1(); u2(); };
  }, []);

  // Label for a category key — custom folder label wins, else taxonomy label.
  const catLabel = (key: string): string =>
    customFolders.find((f) => f.level === 'category' && f.categoryKey === key)?.label ?? categoryLabel(key);
  const isCustomCat = (key: string): boolean =>
    customFolders.some((f) => f.level === 'category' && f.categoryKey === key);

  const [cat, dtype] = vpath;

  // Compute the rows for the current virtual folder.
  const { folders, files } = useMemo(() => {
    if (vpath.length === 0) {
      const byCat = new Map<string, number>();
      for (const d of docs) {
        const c = d.category || 'other';
        byCat.set(c, (byCat.get(c) ?? 0) + 1);
      }
      // Include custom (manually-created) category folders even when empty.
      for (const cf of customFolders) {
        if (cf.level === 'category' && !byCat.has(cf.categoryKey)) byCat.set(cf.categoryKey, 0);
      }
      return {
        folders: [...byCat.entries()]
          .map(([id, count]) => ({ id, label: catLabel(id), count, custom: count === 0 && isCustomCat(id) }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        files: [] as DocRow[],
      };
    }
    if (vpath.length === 1) {
      const inCat = docs.filter((d) => (d.category || 'other') === cat);
      const byType = new Map<string, number>();
      for (const d of inCat) {
        const t = d.docType || 'uncategorized';
        byType.set(t, (byType.get(t) ?? 0) + 1);
      }
      for (const cf of customFolders) {
        if (cf.level === 'docType' && cf.categoryKey === cat && cf.docType && !byType.has(cf.docType)) {
          byType.set(cf.docType, 0);
        }
      }
      return {
        folders: [...byType.entries()]
          .map(([id, count]) => ({ id, label: id, count, custom: count === 0 }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        files: [] as DocRow[],
      };
    }
    const f = docs
      .filter((d) => (d.category || 'other') === cat && (d.docType || 'uncategorized') === dtype)
      .filter((d) => !search || (d.originalName ?? '').toLowerCase().includes(search.toLowerCase()))
      .slice()
      .sort((a, b) => {
        let cmp = 0;
        if (sortKey === 'name') cmp = (a.originalName ?? '').localeCompare(b.originalName ?? '');
        else if (sortKey === 'entity') cmp = (a.filingEntity ?? '').localeCompare(b.filingEntity ?? '');
        else if (sortKey === 'size') cmp = (a.sizeBytes ?? 0) - (b.sizeBytes ?? 0);
        else if (sortKey === 'status') cmp = (a.status ?? '').localeCompare(b.status ?? '');
        return cmp * sortDir;
      });
    return { folders: [], files: f };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, vpath, cat, dtype, search, sortKey, sortDir, customFolders]);

  function clickSort(k: FileSortKey) {
    if (k === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(k); setSortDir(1); }
  }

  async function purge(d: DocRow) {
    // storage object
    if (d.storagePath) {
      const obj = d.storagePath.replace(/^gs:\/\/[^/]+\//, '');
      await deleteObject(storageRef(storage, obj)).catch(() => {});
    }
    // financial rows
    try {
      const fin = await getDocs(query(collection(db, 'financialRecords'), where('docId', '==', d.id)));
      await Promise.all(fin.docs.map((x) => deleteDoc(x.ref).catch(() => {})));
    } catch {
      /* ignore */
    }
    // the doc record
    await deleteDoc(fsDoc(db, 'documents', d.id)).catch(() => {});
  }

  async function deleteSelected() {
    if (!isAdmin || selected.size === 0) return;
    const targets = files.filter((f) => selected.has(f.id));
    if (!confirm(`Delete ${targets.length} document(s)? This removes the file, its record, and any extracted financial rows. Cannot be undone.`)) return;
    let n = 0;
    setProgress('Deleting…');
    for (const d of targets) {
      await purge(d);
      setProgress(`Deleting… ${++n}/${targets.length}`);
    }
    setProgress(`Removed ${n} document(s).`);
    setSelected(new Set());
    setTimeout(() => setProgress(null), 2500);
  }

  // "Move" = reclassify. Updating category/docType moves the doc into the
  // target virtual folder (and updates its classification everywhere).
  async function moveSelected(toCategory: string, toDocType: string) {
    if (!isAdmin || selected.size === 0) return;
    const ids = [...selected];
    setMoveOpen(false);
    let n = 0;
    setProgress('Moving…');
    for (const id of ids) {
      const fields: Record<string, unknown> = {
        category: toCategory,
        reclassifiedManually: true,
        reclassifiedAt: serverTimestamp(),
      };
      if (toDocType.trim()) fields.docType = toDocType.trim();
      await updateDoc(fsDoc(db, 'documents', id), fields).catch(() => {});
      setProgress(`Moving… ${++n}/${ids.length}`);
    }
    setProgress(`Moved ${n} document(s) to ${categoryLabel(toCategory)}.`);
    setSelected(new Set());
    setTimeout(() => setProgress(null), 2500);
  }

  async function newFolder() {
    if (!isAdmin) return;
    if (vpath.length === 0) {
      const name = (prompt('New category folder name:') ?? '').trim();
      if (!name) return;
      await addDoc(collection(db, 'classifiedFolders'), {
        level: 'category',
        categoryKey: slugify(name),
        label: name,
        createdAt: serverTimestamp(),
      }).catch((e) => setProgress(e instanceof Error ? e.message : 'Failed'));
    } else if (vpath.length === 1) {
      const name = (prompt(`New doc-type folder under "${catLabel(cat!)}":`) ?? '').trim();
      if (!name) return;
      await addDoc(collection(db, 'classifiedFolders'), {
        level: 'docType',
        categoryKey: cat,
        docType: name,
        label: name,
        createdAt: serverTimestamp(),
      }).catch((e) => setProgress(e instanceof Error ? e.message : 'Failed'));
    }
  }

  async function removeCustomFolder(level: 'category' | 'docType', key: string) {
    const f = customFolders.find(
      (x) => x.level === level && x.categoryKey === (level === 'category' ? key : cat) && (level === 'category' || x.docType === key),
    );
    if (!f) return;
    if (!confirm('Remove this empty folder? (Documents are never deleted by this.)')) return;
    await deleteDoc(fsDoc(db, 'classifiedFolders', f.id)).catch(() => {});
  }

  async function open(d: DocRow) {
    if (!d.storagePath) return;
    try {
      const obj = d.storagePath.replace(/^gs:\/\/[^/]+\//, '');
      window.open(await getDownloadURL(storageRef(storage, obj)), '_blank', 'noopener');
    } catch {
      /* ignore */
    }
  }

  const allChecked = files.length > 0 && files.every((f) => selected.has(f.id));

  return (
    <div className="space-y-3">
      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-1 text-xs">
        <button onClick={() => { setVpath([]); setSelected(new Set()); }} className="font-medium text-[color:var(--sfp-primary)] hover:underline" type="button">
          All categories
        </button>
        {cat && (
          <>
            <span className="text-stone-400">/</span>
            <button onClick={() => { setVpath([cat]); setSelected(new Set()); }} className="text-[color:var(--sfp-primary)] hover:underline" type="button">
              {catLabel(cat)}
            </button>
          </>
        )}
        {dtype && (
          <>
            <span className="text-stone-400">/</span>
            <span className="text-stone-600">{dtype}</span>
          </>
        )}
        {vpath.length > 0 && (
          <button onClick={() => { setVpath(vpath.slice(0, -1)); setSelected(new Set()); }} className="ml-2 rounded border border-stone-300 px-2 py-0.5 text-[10px] hover:bg-stone-50" type="button">
            ↑ Up
          </button>
        )}
        {isAdmin && vpath.length < 2 && (
          <button onClick={() => void newFolder()} className="ml-1 rounded border border-stone-300 px-2 py-0.5 text-[10px] hover:bg-stone-50" type="button">
            📁 New {vpath.length === 0 ? 'category' : 'doc-type'} folder
          </button>
        )}
        {vpath.length === 2 && (
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter…" className="ml-auto h-7 w-40 rounded border border-stone-300 px-2 text-xs" />
        )}
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-[#9C4A3A]/30 bg-[#9C4A3A]/5 px-3 py-2 text-xs">
          <span className="font-medium text-stone-700">{selected.size} selected</span>
          {isAdmin && (
            <button onClick={() => setMoveOpen(true)} className="rounded bg-[color:var(--sfp-primary)] px-3 py-1 font-medium text-white hover:opacity-90" type="button">
              Move to…
            </button>
          )}
          {isAdmin && (
            <button onClick={() => void deleteSelected()} className="rounded bg-[#9C4A3A] px-3 py-1 font-medium text-white hover:bg-[#7E3A2D]" type="button">
              Delete selected
            </button>
          )}
          <button onClick={() => setSelected(new Set())} className="text-stone-500 hover:text-stone-800" type="button">clear</button>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-stone-200 px-4 py-2 text-xs text-stone-500">
          <span>{loading ? 'Loading…' : folders.length ? `${folders.length} folder(s)` : `${files.length} document(s)`}</span>
          {progress && <span className="text-[#9C4A3A]">{progress}</span>}
        </div>

        {/* Folder grid (category or docType level) */}
        {folders.length > 0 && (
          <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 lg:grid-cols-4">
            {folders.map((f) => (
              <div
                key={f.id}
                className="group relative flex items-center gap-2 rounded-xl border border-stone-200 px-3 py-2 hover:border-stone-400 hover:bg-stone-50"
              >
                <button type="button" onClick={() => { setVpath([...vpath, f.id]); setSelected(new Set()); }} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                  <span className="text-xl">📁</span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-stone-800">{f.label}</span>
                    <span className="block text-[10px] text-stone-400">{f.count} doc(s){f.custom ? ' · custom' : ''}</span>
                  </span>
                </button>
                {isAdmin && f.custom && f.count === 0 && (
                  <button
                    type="button"
                    title="Remove empty folder"
                    onClick={(e) => { e.stopPropagation(); void removeCustomFolder(vpath.length === 0 ? 'category' : 'docType', f.id); }}
                    className="hidden h-5 w-5 shrink-0 place-items-center rounded text-[#9C4A3A] hover:bg-[#9C4A3A] hover:text-white group-hover:grid"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* File list (leaf level) */}
        {vpath.length === 2 && (
          files.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-stone-400">No documents here.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="w-8 px-3 py-2">
                    <input type="checkbox" checked={allChecked} onChange={() => setSelected(allChecked ? new Set() : new Set(files.map((f) => f.id)))} />
                  </th>
                  <SortTh label="Name" k="name" sortKey={sortKey} sortDir={sortDir} onClick={clickSort} />
                  <SortTh label="Entity" k="entity" sortKey={sortKey} sortDir={sortDir} onClick={clickSort} />
                  <SortTh label="Size" k="size" sortKey={sortKey} sortDir={sortDir} onClick={clickSort} />
                  <SortTh label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={clickSort} />
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {files.map((d) => {
                  const sel = selected.has(d.id);
                  return (
                    <tr key={d.id} className={`border-b border-stone-100 ${sel ? 'bg-[color:var(--sfp-primary)]/5' : 'hover:bg-stone-50'}`}>
                      <td className="px-3 py-1.5">
                        <input type="checkbox" checked={sel} onChange={() => {
                          const n = new Set(selected);
                          if (n.has(d.id)) n.delete(d.id); else n.add(d.id);
                          setSelected(n);
                        }} />
                      </td>
                      <td className="px-3 py-1.5">
                        <button onClick={() => void open(d)} className="text-left text-stone-800 hover:text-[color:var(--sfp-primary)]" type="button" title="Open / download">
                          {d.originalName ?? d.id}
                        </button>
                      </td>
                      <td className="px-3 py-1.5 text-xs text-stone-600">{d.filingEntity ?? '—'}</td>
                      <td className="px-3 py-1.5 text-xs text-stone-600">{d.sizeBytes != null ? bytesHuman(d.sizeBytes) : '—'}</td>
                      <td className="px-3 py-1.5">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] ${d.status === 'ready' ? 'bg-emerald-100 text-emerald-700' : d.status === 'error' ? 'bg-[#9C4A3A]/10 text-[#9C4A3A]' : d.status === 'triage' ? 'bg-orange-100 text-orange-700' : 'bg-stone-100 text-stone-600'}`}>
                          {d.status ?? '—'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">
                        <Link to={`/documents/${d.id}`} className="text-xs text-[color:var(--sfp-primary)] hover:underline">Open</Link>
                        {isAdmin && (
                          <button
                            onClick={() => { setSelected(new Set([d.id])); setMoveOpen(true); }}
                            className="ml-3 text-xs text-stone-600 hover:underline"
                            type="button"
                          >
                            Move
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => { setSelected(new Set([d.id])); setTimeout(() => void deleteSelected(), 0); }}
                            className="ml-3 text-xs text-[#9C4A3A] hover:underline"
                            type="button"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </div>

      {moveOpen && (
        <MoveModal
          count={selected.size}
          defaultCategory={(cat as Category) ?? 'other'}
          defaultDocType={dtype ?? ''}
          customCategories={customFolders.filter((f) => f.level === 'category').map((f) => ({ value: f.categoryKey, label: f.label }))}
          onCancel={() => setMoveOpen(false)}
          onMove={(c, t) => void moveSelected(c, t)}
        />
      )}
    </div>
  );
}

function SortTh({
  label,
  k,
  sortKey,
  sortDir,
  onClick,
}: {
  label: string;
  k: FileSortKey;
  sortKey: FileSortKey;
  sortDir: 1 | -1;
  onClick: (k: FileSortKey) => void;
}) {
  return (
    <th className="px-3 py-2">
      <button onClick={() => onClick(k)} className="inline-flex items-center gap-1 hover:text-stone-800" type="button">
        {label}
        {sortKey === k && <span>{sortDir === 1 ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

function MoveModal({
  count,
  defaultCategory,
  defaultDocType,
  customCategories,
  onCancel,
  onMove,
}: {
  count: number;
  defaultCategory: Category;
  defaultDocType: string;
  customCategories: Array<{ value: string; label: string }>;
  onCancel: () => void;
  onMove: (category: string, docType: string) => void;
}) {
  const [category, setCategory] = useState<string>(defaultCategory);
  const [docType, setDocType] = useState<string>(defaultDocType);
  const builtinKeys = new Set(CATEGORIES as readonly string[]);
  const extraCats = customCategories.filter((c) => !builtinKeys.has(c.value));
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="text-sm font-semibold text-stone-800">Move {count} document(s)</h2>
        <p className="mt-1 text-xs text-stone-500">
          Moving a document changes its classification. It will leave this folder and appear under
          the category and doc type you choose.
        </p>
        <div className="mt-4 space-y-3 text-xs">
          <label className="flex flex-col">
            <span className="text-stone-500">Category (folder)</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded border border-stone-300 px-2 py-1.5 text-sm">
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{categoryLabel(c)}</option>
              ))}
              {extraCats.length > 0 && (
                <optgroup label="Custom folders">
                  {extraCats.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
          <label className="flex flex-col">
            <span className="text-stone-500">Doc type (sub-folder) — leave blank to keep each file's type</span>
            <input value={docType} onChange={(e) => setDocType(e.target.value)} placeholder="e.g. capital_account" className="rounded border border-stone-300 px-2 py-1.5 text-sm" />
          </label>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onCancel} className="text-xs text-stone-500 hover:text-stone-800" type="button">Cancel</button>
          <button onClick={() => onMove(category, docType)} className="rounded bg-[color:var(--sfp-primary)] px-4 py-1.5 text-xs font-medium text-white" type="button">
            Move
          </button>
        </div>
      </div>
    </div>
  );
}

function bytesHuman(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
